// File: client/src/variant/effectEngine.js
//
// The effect engine: a registry of CardEffect handlers keyed by `kind`, plus the
// turn-orchestration primitives the variant layer uses to apply cards around the
// chess engine (DESIGN PRINCIPLE #1: the chess engine stays the source of truth;
// the engine here only computes directives — it never mutates the board itself).
//
// Effects self-register by importing this module and calling registerEffect(...),
// OR are registered centrally in registerAllEffects() below. Either way there is
// NO giant switch statement (DESIGN PRINCIPLE #4): dispatch is a Map lookup on
// `kind`.

import { NULL_EFFECT, isValidEffect } from './CardEffect';

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

const registry = new Map();

/**
 * Register a CardEffect handler. Last registration for a given kind wins (lets a
 * test or a later module override). Throws on a malformed effect so mistakes are
 * caught at startup, not mid-game.
 * @param {object} effect a CardEffect (must have a string `kind`)
 */
export function registerEffect(effect) {
  if (!isValidEffect(effect)) {
    throw new Error('registerEffect: effect must be an object with a non-empty string `kind`');
  }
  registry.set(effect.kind, effect);
}

/**
 * Look up the effect for a kind, falling back to NULL_EFFECT (never returns
 * undefined, so a played card can never crash the turn controller).
 * @param {string} kind
 * @returns {object} a CardEffect
 */
export function getEffect(kind) {
  return registry.get(kind) || NULL_EFFECT;
}

/** Resolve the effect for a full card definition object. */
export function getEffectForCard(card) {
  const kind = card && card.effect_json && card.effect_json.kind;
  return getEffect(kind);
}

/** Test/diagnostic helper: list registered kinds. */
export function registeredKinds() {
  return Array.from(registry.keys());
}

/** Test helper: wipe the registry (used to isolate unit tests). */
export function __resetRegistry() {
  registry.clear();
}

// ---------------------------------------------------------------------------
// Patch merging
// ---------------------------------------------------------------------------

/**
 * Merge a hook patch into the running turn directive. Numeric fields accumulate;
 * booleans OR together; arrays concatenate; messages collect. Unknown/undefined
 * patches are ignored. Pure — returns a new object, never mutates inputs.
 * @param {object} acc the accumulated directive
 * @param {object|undefined} patch a hook return value
 * @returns {object}
 */
export function mergePatch(acc, patch) {
  if (!patch) return acc;
  const next = { ...acc };

  if (typeof patch.bonusMoves === 'number') {
    next.bonusMoves = (next.bonusMoves || 0) + patch.bonusMoves;
  }
  if (typeof patch.skipOpponentTurns === 'number') {
    next.skipOpponentTurns = (next.skipOpponentTurns || 0) + patch.skipOpponentTurns;
  }
  if (patch.swapControl) next.swapControl = true;
  if (patch.tradeHands) next.tradeHands = true;
  if (Array.isArray(patch.immediateMoves)) {
    next.immediateMoves = [...(next.immediateMoves || []), ...patch.immediateMoves];
  }
  if (patch.rule && typeof patch.rule === 'object') {
    next.rules = { ...(next.rules || {}), ...patch.rule };
  }
  if (patch.message) {
    next.messages = [...(next.messages || []), patch.message];
  }
  return next;
}

/** A fresh, empty turn directive. */
export function emptyDirective() {
  return {
    bonusMoves: 0,
    skipOpponentTurns: 0,
    swapControl: false,
    tradeHands: false,
    immediateMoves: [],
    rules: {},
    messages: [],
  };
}

// ---------------------------------------------------------------------------
// Turn state
// ---------------------------------------------------------------------------

/**
 * Per-turn scratch state the engine tracks for the active player. Created at the
 * start of each turn (one normal move = movesAllowed 1). Effects raise
 * movesAllowed via bonusMoves; the controller enforces the "no same piece twice"
 * rule using lastMovedPieceKey.
 */
export function createTurnState(playerColor) {
  return {
    playerColor,
    movesAllowed: 1,    // a normal turn is exactly one move
    movesMade: 0,
    lastMovedPieceKey: null, // identity of the piece moved on the previous bonus move
    rules: {},          // turn-scoped rule flags accumulated from played cards
    cardPlayed: false,  // at most one card may be played per turn
  };
}

/**
 * Apply a played card's onPlay hook and fold its directive into the given turn
 * state, returning { directive, turnState }. Does NOT touch the chess board.
 *
 * @param {object} card     full card definition
 * @param {object} ctx      { gameState, playerColor, opponentColor }
 * @param {object} turnState mutable turn state from createTurnState
 * @returns {{directive: object, turnState: object}}
 */
export function applyCardOnPlay(card, ctx, turnState) {
  const effect = getEffectForCard(card);
  let directive = emptyDirective();

  if (typeof effect.onPlay === 'function') {
    const patch = effect.onPlay({
      card,
      gameState: ctx.gameState,
      turnState,
      playerColor: ctx.playerColor,
      opponentColor: ctx.opponentColor,
    });
    directive = mergePatch(directive, patch);
  }

  // Fold turn-affecting directives into turnState.
  const nextTurnState = {
    ...turnState,
    cardPlayed: true,
    movesAllowed: turnState.movesAllowed + (directive.bonusMoves || 0),
    rules: { ...turnState.rules, ...directive.rules },
  };

  return { directive, turnState: nextTurnState };
}

export default {
  registerEffect,
  getEffect,
  getEffectForCard,
  registeredKinds,
  mergePatch,
  emptyDirective,
  createTurnState,
  applyCardOnPlay,
};
